import { useMemo, useState } from 'react';
import { Divider, Link, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

import AccountUploadRow from './AccountUploadRow';
import EnableAutoStatementsBanner from './EnableAutoStatementsBanner';

import STATEMENT_UPLOAD_ANALYTICS_EVENTS from 'services/statementUploadTasks/constants/analytics';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import Dialog from 'modules/common/components/MUI/Dialog';
import AutoStatementsSuccessModal from 'modules/Settings/BankAccountsCombined/components/AutoStatementsSuccessModal';
import { StatementUploadTaskContext } from 'modules/common/components/StatementUpload/StatementUpload.interface';
import useUpdateOperatorTaskStatus from 'services/operatorTasks/mutations/useUpdateOperatorTaskStatus';
import { TaskStatus } from 'modules/common/components/LaunchpadProgress/LaunchpadProgress.interface';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';
import useCombinedBankData from 'modules/Settings/BankAccountsCombined/hooks/useCombinedBankData';
import { CombinedAccount } from 'modules/Settings/BankAccountsCombined/hooks/useCombinedBankData/index.interface';
import { BankAccountSubType } from 'services/hurdlr/queries/useHurdlrPlaidItems/useHurdlrPlaidItems.interface';
import { Document } from 'modules/common/interfaces/documents';
import useQuilttConnect from 'modules/common/components/StatementUpload/hooks/useQuilttConnect';

interface StatementUploadModalProps {
    readonly open: boolean;
    readonly onClose: () => void;
    readonly taskId: string;
    readonly context: StatementUploadTaskContext;
    readonly documents: Document[];
    readonly isResolvable?: boolean;
    readonly connectorId?: string | null;
    readonly connectionId?: string | null;
    readonly isConnectionLost?: boolean;
    readonly onTaskCompleted: (taskId: string) => void;
}

interface EnrichedAccount {
    // Stable identity for the row — its React key and accountDocMap key.
    // OFF: the account mask. ON: `${mask}-${year}-${month}` so the same mask can
    // appear once per (account, month).
    rowKey: string;
    accountMask: string;
    accountName: string;
    accountSubType: BankAccountSubType;
    plaidInstitutionId: string;
    year: number;
    month: number;
}

export default function StatementUploadModal({
    open,
    onClose,
    taskId,
    context,
    documents,
    isResolvable,
    connectorId,
    connectionId,
    isConnectionLost,
    onTaskCompleted,
}: StatementUploadModalProps) {
    const isPerAccountDatesEnabled = useStatsigGate(FeatureGates.StatementPerAccountDates);

    const [internalOpen, setInternalOpen] = useState(open);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const updateTaskStatus = useUpdateOperatorTaskStatus();
    const { sendTrackingEvent } = useEventTracking();
    const { institutions } = useCombinedBankData();

    const { year, month, accounts, institution_name } = context;
    const monthLabel = dayjs()
        .month(month - 1)
        .format('MMMM');

    // Each task is tied to a single institution — all accounts under a task share
    // the same plaid_institution_id, so we use the first account's ID for the lookup.
    const enrichedAccounts = useMemo((): EnrichedAccount[] => {
        const plaidInstitutionId = accounts[0]?.plaid_institution_id;
        const institution = plaidInstitutionId
            ? institutions.find((inst) => inst.institutionId === plaidInstitutionId)
            : undefined;

        const combinedAccountMap = new Map<string, CombinedAccount>();
        institution?.accounts.forEach((acc) => {
            combinedAccountMap.set(acc.apiAccountNo, acc);
        });

        const enrich = (account: (typeof accounts)[number], entryYear: number, entryMonth: number): EnrichedAccount => {
            const combined = combinedAccountMap.get(account.account_mask);
            return {
                rowKey: isPerAccountDatesEnabled
                    ? `${account.account_mask}-${entryYear}-${entryMonth}`
                    : account.account_mask,
                accountMask: account.account_mask,
                accountName: combined?.name ?? `Account ${account.account_mask}`,
                accountSubType: combined?.subType ?? BankAccountSubType.Checking,
                plaidInstitutionId: account.plaid_institution_id ?? plaidInstitutionId ?? '',
                year: entryYear,
                month: entryMonth,
            };
        };

        if (isPerAccountDatesEnabled) {
            // One row per accounts[] entry — the same mask may repeat across months.
            return accounts.map((account) => enrich(account, account.year ?? year, account.month ?? month));
        }

        // OFF — today's behavior: one row per mask using the top-level year/month.
        // Dedupe by mask so multi-entry backend data never regresses the OFF look.
        const seenMasks = new Set<string>();
        return accounts
            .filter((account) => {
                if (seenMasks.has(account.account_mask)) {
                    return false;
                }
                seenMasks.add(account.account_mask);
                return true;
            })
            .map((account) => enrich(account, year, month));
    }, [accounts, institutions, isPerAccountDatesEnabled, year, month]);

    // Build a map of documents per row, filtered by filename prefix scoped to
    // each row's own year/month (per-account when the gate is on, top-level otherwise).
    const accountDocMap = useMemo(() => {
        const map = new Map<string, Document[]>();
        enrichedAccounts.forEach((acc) => {
            const prefix = `${acc.year}-${acc.month}-${acc.plaidInstitutionId}-${acc.accountMask}-`;
            map.set(
                acc.rowKey,
                documents.filter((doc) => doc.file_name.startsWith(prefix))
            );
        });
        return map;
    }, [documents, enrichedAccounts]);

    const allAccountsHaveFiles =
        enrichedAccounts.length > 0 &&
        enrichedAccounts.every((acc) => (accountDocMap.get(acc.rowKey) ?? []).length > 0);

    const handleSave = () => {
        sendTrackingEvent(STATEMENT_UPLOAD_ANALYTICS_EVENTS.SUBMIT_STATEMENTS_CLICKED, {
            institution_name,
            task_id: taskId,
            year,
            month,
            source: 'upload_modal',
        });
        // Query invalidation is intentionally deferred to the caller so it can animate the row
        // out before the task disappears from query data.
        updateTaskStatus.mutate(
            { id: taskId, status: TaskStatus.COMPLETED },
            {
                onSuccess: () => {
                    onTaskCompleted(taskId);
                    onClose();
                },
            }
        );
    };

    const accountsAlreadyQuilttEnabled = accounts.some((a) => a.is_quiltt);
    const showEnableAutoStatements = isResolvable && connectorId && !accountsAlreadyQuilttEnabled && !isConnectionLost;

    // Quiltt connect hook lives here (above the Dialog) so its callbacks remain registered
    // even when we close the Dialog to avoid stacked modals during the Quiltt flow.
    const { open: openQuilttConnector, isConnecting } = useQuilttConnect({
        connectorId: connectorId ?? '',
        connectionId,
        institutionName: institution_name,
        sourceSurface: 'statement_upload_modal',
        onSuccess: () => {
            updateTaskStatus.mutate(
                { id: taskId, status: TaskStatus.COMPLETED },
                {
                    onSuccess: () => {
                        setShowSuccessModal(true);
                    },
                }
            );
        },
        onAbort: () => {
            // Reopen the upload modal so the user can try again
            setInternalOpen(true);
        },
        onError: () => {
            setInternalOpen(true);
        },
    });

    const handleEnableClick = () => {
        // Close the upload modal before opening Quiltt to avoid stacked modals
        setInternalOpen(false);
        openQuilttConnector();
    };

    const handleSuccessModalClose = () => {
        setShowSuccessModal(false);
        onTaskCompleted(taskId);
        onClose();
    };

    return (
        <>
            <Dialog
                open={internalOpen}
                onClose={onClose}
                heading={`${institution_name} statement upload`}
                maxWidth="sm"
                fullWidth
                positiveAction={{
                    label: 'Submit statements',
                    onClick: handleSave,
                    disabled: !allAccountsHaveFiles,
                    loading: updateTaskStatus.isPending,
                }}
                banner={
                    showEnableAutoStatements ? (
                        <>
                            <EnableAutoStatementsBanner
                                institutionName={institution_name}
                                onEnableClick={handleEnableClick}
                                isConnecting={isConnecting}
                            />
                            <Divider />
                        </>
                    ) : undefined
                }>
                <Stack spacing={2}>
                    <Typography variant="body1">
                        {isPerAccountDatesEnabled ? (
                            'Upload your bank statements'
                        ) : (
                            <>
                                Upload your bank statements for{' '}
                                <strong>
                                    {monthLabel} {year}
                                </strong>
                            </>
                        )}
                    </Typography>

                    <Stack divider={<Divider />}>
                        {enrichedAccounts.map((account) => (
                            <AccountUploadRow
                                key={account.rowKey}
                                accountMask={account.accountMask}
                                accountName={account.accountName}
                                accountSubType={account.accountSubType}
                                plaidInstitutionId={account.plaidInstitutionId}
                                year={account.year}
                                month={account.month}
                                documents={accountDocMap.get(account.rowKey) ?? []}
                                taskId={taskId}
                                institutionName={institution_name}
                                showDate={isPerAccountDatesEnabled}
                            />
                        ))}
                    </Stack>

                    <Typography variant="body1" color="text.secondary">
                        Need to remove an account? Go to{' '}
                        <Link href="/settings/bank-accounts" target="_blank" rel="noopener noreferrer" color="inherit">
                            Bank Settings
                        </Link>
                    </Typography>
                </Stack>
            </Dialog>
            <AutoStatementsSuccessModal
                open={showSuccessModal}
                onClose={handleSuccessModalClose}
                institutionName={institution_name}
            />
        </>
    );
}
